// @ts-nocheck
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { 
  Trophy, Users, Calendar, FileText, Ticket, 
  ClipboardList, Database, UserCheck, Shield, Loader2,
  PlusCircle, BarChart3, Timer, Plane, Warehouse, 
  ListChecks, CreditCard, Mail, Package, Zap
} from 'lucide-react';

const DemoHubPage: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [purchasingPacket, setPurchasingPacket] = useState(false);

  const demoEventId = 'f83c2769-b4aa-4ba9-9e3e-5e205fd40047';

  const handleDemoPurchasePacket = async () => {
    if (!profile?.id) {
      toast.error('Please log in to purchase a demo packet');
      return;
    }
    setPurchasingPacket(true);
    try {
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('name')
        .eq('id', demoEventId)
        .single();
      if (eventError) throw eventError;
      const { error: packetError } = await supabase
        .from('generated_packets')
        .insert({
          event_id: demoEventId,
          event_name: eventData?.name || 'Demo Event',
          template_name: 'Demo Roster Packet',
          generated_by: profile.id,
          status: 'completed',
          stripe_session_id: 'DEMO_' + Date.now(),
          packet_file_url: null
        })
        .select()
        .single();
      if (packetError) throw packetError;
      toast.success('Demo packet purchased! Redirecting to My Packets...');
      setTimeout(() => navigate('/demo/packets'), 1000);
    } catch (error) {
      console.error('Demo purchase error:', error);
      toast.error('Failed to create demo packet');
    } finally {
      setPurchasingPacket(false);
    }
  };

  const Section = ({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) => (
    <div className="mb-10">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <Icon className="w-5 h-5 text-[#BC8D09]" />
        {title}
      </h2>
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
        {children}
      </div>
    </div>
  );

  const DemoCard = ({ title, description, to, icon: Icon, onClick }: { title: string; description: string; to?: string; icon: any; onClick?: () => void }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {to ? (
          <Link to={to}>
            <Button className="w-full" size="sm">
              <Icon className="w-4 h-4 mr-2" />
              Open
            </Button>
          </Link>
        ) : (
          <Button className="w-full" size="sm" onClick={onClick} disabled={purchasingPacket}>
            {purchasingPacket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Icon className="w-4 h-4 mr-2" />}
            {purchasingPacket ? 'Processing...' : 'Open'}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Demo Banner */}
      <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-8">
        <h1 className="text-2xl font-bold text-yellow-800">🎮 Full Platform Demo</h1>
        <p className="text-yellow-700">
          Explore every feature of the event management platform. 
          <Badge className="ml-2">{profile?.role || 'Guest'}</Badge>
        </p>
      </div>

      {/* Event Creation */}
      <Section title="Event Creation & Setup" icon={PlusCircle}>
        <DemoCard title="Create New Event" description="Build an event from scratch with full configuration" to="/demo/create-event" icon={PlusCircle} />
        <DemoCard title="Event Management" description="Edit settings, pricing, divisions, and details" to={`/demo/event-management/${demoEventId}`} icon={Calendar} />
        <DemoCard title="Seed Demo Data" description="Generate teams, coaches, and registrations" to="/demo/data" icon={Database} />
        <DemoCard title="Browse Demo Events" description="View the public-facing events page" to="/demo/events" icon={Calendar} />
      </Section>

      {/* Registration & Checkout */}
      <Section title="Registration & Checkout" icon={CreditCard}>
        <DemoCard title="Team Registration" description="Multi-step wizard with division/age-group pricing" to={`/demo/events`} icon={Users} />
        <DemoCard title="Spectator Tickets" description="Ticket purchase with extras, add-ons, and upsells" to={`/events/${demoEventId}/spectator-tickets`} icon={Ticket} />
        <DemoCard title="View Registrations" description="Admin view of all registrations, payments, and statuses" to="/demo/registrations" icon={ClipboardList} />
        <DemoCard title="Check-In Scanner" description="QR code scanning and team check-in on game day" to="/demo/check-in" icon={ClipboardList} />
      </Section>

      {/* Brackets & Tournaments */}
      <Section title="Brackets & Tournament Formats" icon={Trophy}>
        <DemoCard title="Tournament Setup" description="Create brackets: Single/Double Elim, Round Robin, Pool Play" to="/demo/tournaments" icon={Trophy} />
        <DemoCard title="Live Scoring" description="Enter scores, advance winners, update standings" to={`/demo/scoring/${demoEventId}`} icon={FileText} />
        <DemoCard title="Browse Brackets" description="Public-facing bracket and pool play views" to="/tournaments" icon={Trophy} />
      </Section>

      {/* Scheduling & Scoring */}
      <Section title="Scheduling & Live Scoring" icon={Timer}>
        <DemoCard title="Scheduling Hub" description="Court/field scheduling, time-slot management, calendar views" to="/demo/scheduling" icon={Timer} />
        <DemoCard title="Live Scoring Interface" description="Game-day scoring with staff-friendly views" to={`/demo/scoring/${demoEventId}`} icon={Zap} />
      </Section>

      {/* Rankings & Leaderboards */}
      <Section title="Rankings & Leaderboards" icon={BarChart3}>
        <DemoCard title="Leaderboard Builder" description="Database-driven scoring rules, no-code updates" to="/demo/leaderboards" icon={BarChart3} />
      </Section>

      {/* Operations Suite */}
      <Section title="Operations Suite" icon={Warehouse}>
        <DemoCard title="Staffing" description="Roles → Directory → Needs → Assignments + gap analysis" to="/demo/staffing" icon={UserCheck} />
        <DemoCard title="Officials" description="Coverage grids, availability tracking, certifications" to="/demo/officials" icon={Shield} />
        <DemoCard title="Scorekeepers" description="Scorekeeper assignments and coverage" to="/demo/scorekeepers" icon={ClipboardList} />
        <DemoCard title="Travel" description="Flights, hotels, car rentals, budgets vs actuals" to="/demo/travel" icon={Plane} />
        <DemoCard title="Inventory & Signage" description="Warehouse tracking, venue allocations, transfers" to="/demo/inventory" icon={Warehouse} />
        <DemoCard title="Checklists & Notes" description="Template-driven tasks, overdue alerts, team notes" to="/demo/checklists" icon={ListChecks} />
      </Section>

      {/* Automated Fulfillment */}
      <Section title="Automated Fulfillment & Communications" icon={Mail}>
        <DemoCard title="Packet Generator" description="Create custom roster packets with CSV upload" to="/demo/packet-generator" icon={FileText} />
        <DemoCard title="Purchase Packet" description="Demo: Buy event roster packets (no payment)" icon={Package} onClick={handleDemoPurchasePacket} />
        <DemoCard title="My Packets" description="View purchased and generated packets" to="/demo/packets" icon={FileText} />
      </Section>

      {/* Footer */}
      <div className="mt-12 text-center">
        <Card className="bg-muted/30">
          <CardContent className="py-8">
            <h3 className="text-lg font-semibold mb-2">Interested in RecruitLook Events?</h3>
            <p className="text-muted-foreground mb-4">
              See how our platform can transform your athletic event management
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={() => navigate('/pricing')}>View Pricing</Button>
              <Button onClick={() => navigate('/contact')} variant="outline">Contact Sales</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DemoHubPage;